import { tg } from '@infotech-soe/metaschema';
import {
  Stimulsoft,
  StiViewerOptions,
} from '@infotech-soe/stimulsoft-reports-js/Scripts/stimulsoft.viewer';
import { formatDate } from 'date-fns';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';

import { ViewerOptions } from './types';

export async function getPrintReportInfoOption(): Promise<boolean> {
  try {
    const { value } = await new MerpsApiProvider().options.getOption(
      'PrintReportInfo'
    );
    return !!value;
  } catch {
    return false;
  }
}

/** Activation with using license code */
export function setLicenseKey(key: string): void {
  Stimulsoft.Base.StiLicense.Key = key;
}

/** Set interface language */
export function setLocalization(localizationXml: string): void {
  Stimulsoft.Base.Localization.StiLocalization.setLocalization(localizationXml);
}

function parseColor(color: string): Stimulsoft.System.Drawing.Color {
  // format rgb(0, 255, 20%) or rgba(0, 255, 255, 50%)
  if (color.toLowerCase().includes('rgb')) {
    try {
      const comp = color
        .substring(color.indexOf('(') + 1, color.indexOf(')'))
        .split(',');
      if (comp.length === 3 || comp.length === 4) {
        const rgba = [255, 255, 255, 255];
        comp.forEach((c, idx) => {
          rgba[idx] = c.includes('%')
            ? Math.round((Number(c.trim().substring(0, -1)) / 100) * rgba[idx])
            : Number(c.trim());
        });
        const [r, g, b, a] = rgba;
        return Stimulsoft.System.Drawing.Color.fromArgb(a, r, g, b);
      }
    } catch {
      // do nothing
    }
  }
  // format #RRGGBB
  else if (color.startsWith('#')) {
    return Stimulsoft.System.Drawing.ColorTranslator.fromHtml(color);
  }

  return Stimulsoft.System.Drawing.Color.white;
}

function copyValues<T>(dest: Rec<T>, src?: Partial<Rec<T>>): void {
  if (tg.isSomething(src)) {
    for (const key in src) {
      if (Object.hasOwn(src, key)) {
        dest[key] =
          (dest as any)[key] instanceof Stimulsoft.System.Drawing.Color &&
          tg.isString(src[key])
            ? parseColor(src[key])
            : src[key];
      }
    }
  }
}

/** Update an StiViewerOptions object with new values */
export function updateViewerOptions(
  options: StiViewerOptions,
  props: ViewerOptions
): void {
  const { toolbar, appearance, exports } = options;

  options.width = props.width || options.width;
  options.height = props.height || options.height;

  // Default options
  toolbar.showAboutButton = false;
  toolbar.showOpenButton = false;
  toolbar.showSendEmailButton = false;
  toolbar.showRefreshButton = true;

  copyValues<Stimulsoft.Viewer.StiToolbarOptions>(toolbar, props.toolbar);
  copyValues<Stimulsoft.Viewer.StiAppearanceOptions>(
    appearance,
    props.appearance
  );
  copyValues<Stimulsoft.Viewer.StiExportsOptions>(exports, props.exports);
}

type Rec<T> = Record<keyof T, any>;

function isJsonDate(str: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(str);
}

export function serializeDataForStiReport(
  data: any,
  space?: string | number
): string {
  return JSON.stringify(
    data,
    (_key, value) => {
      if (tg.isDate(value) || isJsonDate(value)) {
        return formatDate(value, `yyyy-MM-dd'T'HH:mm:ss`);
      }
      return value;
    },
    space
  );
}

const PRINT_FOOTER_NAME = '__bravoPrintFooter';

export function addPrintFooter(
  report: Stimulsoft.Report.StiReport,
  printedBy?: string
): void {
  const pages = report.pages;
  for (let index = 0; index < pages.count; index++) {
    const page = pages.getByIndex(index);
    if (page.components.getByName(PRINT_FOOTER_NAME)) continue;

    const footer = new Stimulsoft.Report.Components.StiPageFooterBand();
    footer.name = PRINT_FOOTER_NAME;
    footer.height = 0.35;
    page.components.add(footer);

    const text = new Stimulsoft.Report.Components.StiText();
    text.clientRectangle = new Stimulsoft.System.Drawing.Rectangle(
      0,
      0,
      page.width,
      0.35
    );
    text.text = `${
      printedBy ? `${printedBy}, ` : ''
    }${new Date().toLocaleString('uk-UA')}`;
    text.textBrush = new Stimulsoft.Base.Drawing.StiSolidBrush(
      Stimulsoft.System.Drawing.Color.fromArgb(255, 128, 128, 128)
    );
    footer.components.add(text);
  }
}
